import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconSparkle, IconLock } from './Icons';

// One-time onboarding banner that explains AI mode is on by default and how to turn
// it off. Dismissed via localStorage so users only see it once per device.
//
// Rendered on the /app home screen. Self-contained so dismissing it does not require
// any parent-level state.

const STORAGE_KEY_SEEN = 'mdt_ai_intro_seen';

function hasSeen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_SEEN) === 'true';
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    localStorage.setItem(STORAGE_KEY_SEEN, 'true');
  } catch {
    // Storage unavailable - banner will re-appear next visit. Acceptable.
  }
}

export function AiModeIntroBanner() {
  const [dismissed, setDismissed] = useState(hasSeen());
  if (dismissed) return null;

  function dismiss() {
    markSeen();
    setDismissed(true);
  }

  return (
    <section className="mx-5 mt-4 rounded-2xl bg-accent text-white p-4 shadow-lg shadow-accent/20">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center flex-shrink-0">
          <IconSparkle size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">AI mode is on by default</p>
          <p className="mt-1 text-xs text-white/85 leading-relaxed">
            We use AI to read each worksheet and find the right place for your answers. Photos
            are processed and deleted immediately. The same safeguards apply either way: no
            body images, no faces, documents only.
          </p>
          <p className="mt-2 text-xs text-white/85 leading-relaxed inline-flex items-center gap-1.5">
            <IconLock size={11} /> You can turn it off any time in <Link to="/app/settings" className="underline underline-offset-2 font-medium">Settings</Link>.
          </p>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={dismiss}
          className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition"
        >
          Got it
        </button>
      </div>
    </section>
  );
}
